import { useEffect, useState } from "react";

import { Alert, Button, Tab, TabList, Textarea } from "@realroof/ui";

import { api, canModerate } from "../api";
import { when } from "../format";
import { useSession } from "../session";
import { Table, td, th } from "../table";

export interface ProRow {
  user_id: string;
  kind: "builder" | "agent";
  name: string;
  slug: string;
  status: "pending" | "verified" | "rejected";
  rera_no: string | null;
  since_year: number | null;
  website: string | null;
  about: string | null;
  reason: string | null;
  mobile: string | null;
  email: string | null;
  applied_at: string;
  reviewed_at: string | null;
}

type Filter = ProRow["status"];
const TABS: [Filter, string][] = [
  ["pending", "To check"],
  ["verified", "Verified"],
  ["rejected", "Not verified"],
];
const KIND = { builder: "Builder", agent: "Agent / dealer" };

/** Builders and agents who applied: check the company and RERA no. (state RERA website), then verify. Verified
 * accounts post new projects free and get a public page. */
export function Pros() {
  const { me } = useSession();
  const [filter, setFilter] = useState<Filter>("pending");
  const [rows, setRows] = useState<ProRow[] | null>(null);
  const [refusing, setRefusing] = useState<string | null>(null);
  const [reason, setReason] = useState("");
  const [message, setMessage] = useState<{ kind: "error" | "success"; text: string } | null>(null);

  const load = (f: Filter) =>
    api<ProRow[]>(`/admin/pros?status=${f}`)
      .then(setRows)
      .catch((err: Error) => setMessage({ kind: "error", text: err.message }));
  useEffect(() => {
    setRows(null);
    load(filter);
  }, [filter]);

  async function decide(row: ProRow, verify: boolean) {
    setMessage(null);
    try {
      await api(`/admin/pros/${row.user_id}/${verify ? "verify" : "reject"}`, { method: "POST", body: verify ? undefined : { reason: reason.trim() } });
      setMessage({ kind: "success", text: verify ? `${row.name} is verified; they can post new projects now.` : `${row.name} was told why.` });
      setRefusing(null);
      setReason("");
      await load(filter);
    } catch (err) {
      setMessage({ kind: "error", text: (err as Error).message });
    }
  }

  const moderator = canModerate(me?.staff_role);
  return (
    <section className="flex flex-col gap-4">
      <h1 className="text-2xl font-bold">Builders &amp; agents</h1>
      <p className="m-0 text-sm text-muted-foreground">Check the company and its RERA registration on the state RERA website before verifying. Verified accounts post new projects free.</p>
      <TabList>
        {TABS.map(([f, label]) => (
          <Tab key={f} selected={f === filter} className="flex-none" onClick={() => setFilter(f)}>
            {label}
          </Tab>
        ))}
      </TabList>
      {message && <Alert tone={message.kind}>{message.text}</Alert>}
      {rows === null && <p className="text-muted-foreground">Loading…</p>}
      {rows?.length === 0 && <p className="text-muted-foreground">{filter === "pending" ? "No one waiting." : "Nothing here."}</p>}
      {rows && rows.length > 0 && (
        <Table>
          <thead>
            <tr>
              <th className={th}>Company</th>
              <th className={th}>RERA no.</th>
              <th className={th}>Contact</th>
              <th className={th} />
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.user_id}>
                <td className={`${td} max-w-md`}>
                  <span className="font-semibold">{r.name}</span>
                  <div className="text-sm text-muted-foreground">
                    {KIND[r.kind]}
                    {r.since_year ? ` · since ${r.since_year}` : ""}
                    {r.website && (
                      <>
                        {" · "}
                        <a href={r.website} target="_blank" rel="noreferrer">
                          website
                        </a>
                      </>
                    )}
                  </div>
                  {r.about && <p className="m-0 mt-1 line-clamp-3 text-sm">{r.about}</p>}
                  {r.reason && <div className="text-sm text-muted-foreground">Why not: {r.reason}</div>}
                </td>
                <td className={`${td} font-mono`}>{r.rera_no ?? "—"}</td>
                <td className={`${td} whitespace-nowrap`}>
                  {r.mobile}
                  <div className="text-xs text-muted-foreground">{r.email}</div>
                  <div className="text-xs text-muted-foreground">Applied {when(r.applied_at)}</div>
                </td>
                <td className={td}>
                  {moderator && filter !== "verified" && refusing !== r.user_id && (
                    <div className="flex flex-col gap-2">
                      <Button size="sm" variant="primary" onClick={() => decide(r, true)} aria-label={`Verify ${r.name}`}>
                        Verify
                      </Button>
                      {filter === "pending" && (
                        <Button size="sm" onClick={() => setRefusing(r.user_id)} aria-label={`Don’t verify ${r.name}`}>
                          Don’t verify…
                        </Button>
                      )}
                    </div>
                  )}
                  {moderator && filter === "verified" && (
                    <Button size="sm" variant="ghost" className="text-destructive" onClick={() => setRefusing(r.user_id)} aria-label={`Withdraw ${r.name}`}>
                      Withdraw…
                    </Button>
                  )}
                  {refusing === r.user_id && (
                    <div className="flex min-w-60 flex-col gap-2">
                      <Textarea rows={2} aria-label="Reason they will see" placeholder="They see this" value={reason} onChange={(e) => setReason(e.target.value)} />
                      <div className="flex gap-2">
                        <Button size="sm" variant="destructive" disabled={reason.trim().length < 5} onClick={() => decide(r, false)}>
                          Don’t verify
                        </Button>
                        <Button size="sm" variant="link" onClick={() => setRefusing(null)}>
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </section>
  );
}
